import { useState, useRef, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";

export const CRMCombobox = ({ items, value, onChange, placeholder, onCreate, testId }) => {
  const selected = items.find((i) => i.id === value);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  const exact = items.some((i) => i.name.toLowerCase() === q);

  const pick = (id) => { onChange(id); setQuery(""); setOpen(false); };
  const create = async () => {
    if (!onCreate || creating || !query.trim()) return;
    setCreating(true);
    try { const id = await onCreate(query.trim()); if (id) pick(id); } finally { setCreating(false); }
  };

  if (selected) return (
    <div className="flex items-center justify-between h-9 px-3 rounded-md border border-input bg-background text-sm" data-testid={testId}>
      <span className="truncate">{selected.name}</span>
      <button type="button" onClick={() => onChange("none")} className="p-0.5 rounded hover:bg-muted text-muted-foreground shrink-0" data-testid={`${testId}-clear`}><X className="h-3.5 w-3.5" /></button>
    </div>
  );

  return (
    <div ref={wrapRef} className="relative">
      <Input value={query} placeholder={placeholder} autoComplete="off"
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (filtered.length === 1) pick(filtered[0].id); else if (query.trim() && !exact) create(); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        data-testid={testId} />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg text-sm" data-testid={`${testId}-list`}>
          {filtered.map((i) => (
            <button key={i.id} type="button" onClick={() => pick(i.id)} className="w-full text-left px-3 py-2 hover:bg-muted transition-colors" data-testid={`${testId}-option-${i.id}`}>
              {i.name}
            </button>
          ))}
          {filtered.length === 0 && !q && <p className="px-3 py-2 text-xs text-muted-foreground">Nothing saved yet — start typing to add one</p>}
          {q && filtered.length === 0 && !onCreate && <p className="px-3 py-2 text-xs text-muted-foreground">No matches</p>}
          {q && !exact && onCreate && (
            <button type="button" onClick={create} disabled={creating} className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2 text-primary font-medium border-t transition-colors" data-testid={`${testId}-create`}>
              <Plus className="h-3.5 w-3.5 shrink-0" />{creating ? "Adding…" : `Add "${query.trim()}"`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
